import { Button, Pill, TableCell, TableRow } from '@/shared/components';
import type { AuditLogResponse } from '@/shared/api';
import { formatDateTime } from '@/shared/lib/format';
import { auditEntityLabel } from './systemLabels';

interface Props {
  entry: AuditLogResponse;
  expanded: boolean;
  onToggle: () => void;
}

/**
 * Eine Zeile des Änderungsprotokolls samt ausklappbarem Vorher/Nachher.
 *
 * <p>Der Ausklapp-Bereich hing an einem Alias auf `--color-bg-mute` — das kennt keine der beiden
 * Tokenquellen. Die Zeile hatte also gar keinen Hintergrund und hob sich vom Rest der Tabelle
 * nicht ab. Jetzt `bg-card-alt`, und der Auslöser sagt über `aria-expanded`, was er tut.</p>
 */
export function AuditLogRow({ entry, expanded, onToggle }: Props) {
  const detailId = `audit-diff-${entry.id}`;

  return (
    <>
      <TableRow>
        <TableCell className="whitespace-nowrap font-mono text-xs">
          {formatDateTime(entry.createdAt)}
        </TableCell>
        <TableCell>
          {/* Der Vorgangsname ist eine freie Zeichenkette aus dem Backend und wird bis zu
              dreißig Zeichen lang („PropertyGroupCoordinatesSet"). Ungebremst sprengte er die
              Spalte und schob die Aktion aus der Arbeitsbreite des Einstellungs-Layouts. */}
          <Pill
            variant="outline"
            size="sm"
            className="block max-w-40 truncate"
            title={entry.action}
          >
            {entry.action}
          </Pill>
        </TableCell>
        <TableCell className="text-ink-soft text-xs">
          {auditEntityLabel(entry.entityType)}
          {entry.entityId && (
            <span className="text-ink-faint ml-1 font-mono">{entry.entityId.slice(0, 8)}…</span>
          )}
        </TableCell>
        <TableCell className="text-ink-soft whitespace-nowrap text-xs">
          {entry.userId ? <span className="font-mono">{entry.userId.slice(0, 8)}…</span> : 'System'}
        </TableCell>
        <TableCell className="text-right">
          {entry.diff ? (
            <Button
              variant="ghost"
              className="min-h-11"
              aria-expanded={expanded}
              aria-controls={expanded ? detailId : undefined}
              onClick={onToggle}
            >
              {expanded ? 'Zuklappen' : 'Ansehen'}
            </Button>
          ) : (
            <span className="text-ink-faint text-xs">nichts festgehalten</span>
          )}
        </TableCell>
      </TableRow>
      {expanded && entry.diff && (
        <TableRow>
          <TableCell colSpan={5} className="bg-card-alt p-0">
            <pre
              id={detailId}
              className="text-ink overflow-x-auto whitespace-pre-wrap p-3 font-mono text-xs"
            >
              {tryFormatJson(entry.diff)}
            </pre>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function tryFormatJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}
